import { trapFocus, removeTrapFocus, getFocusableElements, scrollLock } from '@utilities/theme';

export class SlideOutDrawer extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.backdrop = null;
    this.closeButtons = null;
    this.isOpen = false;
    this.identifier = '';
    this.fullIdentifier = '';
    this.isInitialized = false;
    this.observer = null;
    this.escapeListener = null;
    this.focusedElementBeforeOpen = null;
    this.animationDuration = 300;
    this.alphabetLinks = null;
    this.visualFilters = null;
    this.isMoved = false;
    // A tap that lands after upgrade but before initialization queues here;
    // initializeDrawer() consumes it. See consumePendingOpenIntent().
    this.pendingOpen = false;
  }

  connectedCallback() {
    if (this.isInitialized) return;

    if (!this.isMoved && this.parentElement !== document.body) {
      this.moveToBody();
      return;
    }

    this.isInitialized = true;
    this.initializeDrawer();
  }

  moveToBody = () => {
    this.isMoved = true;
    this.remove();
    document.body.appendChild(this);
  }

  initializeDrawer = () => {
    this.setElements();
    if (!this.hasElements()) return;
    this.setVariables();
    this.setEventListeners();
    this.consumePendingOpenIntent();
  }

  /**
   * Open now if a tap landed before this drawer was ready (GC-2437). Two
   * sources: the instance flag open() sets while uninitialized, and the
   * pre-upgrade click capture rendered inline next to the drawer opener
   * (window.__pendingDrawerOpens, keyed by drawer id, stamped with the tap
   * time). A recorded tap older than 10 seconds is discarded - the shopper
   * has moved on, and a drawer that pops open unprompted reads as a bug.
   * @returns {void}
   */
  consumePendingOpenIntent = () => {
    const pendingMap = window.__pendingDrawerOpens;
    let hasInlineIntent = false;

    if (pendingMap && this.fullIdentifier && pendingMap[this.fullIdentifier]) {
      hasInlineIntent = Date.now() - pendingMap[this.fullIdentifier] < 10000;
      delete pendingMap[this.fullIdentifier];
    }

    if (!this.pendingOpen && !hasInlineIntent) return;
    this.pendingOpen = false;
    this.open();
  }

  setElements = () => {
    this.container = this.querySelector('.slide-out-drawer__container');
    this.backdrop = this.querySelector('.slide-out-drawer__backdrop');
    this.closeButtons = this.querySelectorAll('.slide-out-drawer__close');
    this.alphabetLinks = this.querySelectorAll('.slide-out-drawer__alphabetical-navigation-item.has-filters button');
    this.visualFilters = this.querySelectorAll('.slide-out-drawer__content--visual-filter');
  }

  hasElements = () => {
    // Only the container and backdrop are required structural elements. Close
    // buttons may be injected later (e.g. FilterDrawer renders them from a
    // <template> after filters load, then wires them via setupCloseButtonListeners),
    // so their absence at init time is not a failure.
    if (!this.container || !this.backdrop) {
      console.error('Slide-out drawer: Required elements not found', {
        container: !!this.container,
        backdrop: !!this.backdrop,
      });
      return false;
    }
    return true;
  }

  setVariables = () => {
    this.isOpen = this.classList.contains('open');
    this.identifier = this.dataset.identifier;
    this.fullIdentifier = this.getAttribute('id');
  }

  setEventListeners = () => {
    this.addEventListener('facets:initialized', this.handleFacetsInitialized.bind(this));
    for (const button of this.closeButtons) {
      button.addEventListener('click', this.close.bind(this));
    }
    this.backdrop.addEventListener('click', this.close.bind(this));
    this.escapeListener = (event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    };
    this.addEventListener('click', (event) => event.stopPropagation());
    this.initializeAlphabeticalNavigation();
  }

  initializeAlphabeticalNavigation() {
    if (this.alphabetLinks.length === 0) return;
    for (const link of this.alphabetLinks) {
      link.addEventListener('click', this.handleAlphabetClick.bind(this));
    }
  }

  handleFacetsInitialized(event) {
    if (!this.contains(event.target)) return;
    this.initializeDrawer();
  }

  handleAlphabetClick(event) {
    const letter = event.target.dataset.letter;
    if (!letter) return;
    const targetFilter = Array.from(this.visualFilters).find((filter) => filter.dataset.letter === letter);
    if (!targetFilter) return;

    const contentContainer = this.querySelector('.slide-out-drawer__content');
    if (!contentContainer) return;

    const targetPosition = targetFilter.offsetTop - contentContainer.offsetTop;
    contentContainer.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }

  disconnectedCallback = () => {
    if (!this.isMoved) {
      const lock = scrollLock;
      if (lock && typeof lock.disableBodyScrollLock === 'function') {
        lock.disableBodyScrollLock();
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }

      document.removeEventListener('keydown', this.escapeListener);
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
    }
    this.isInitialized = false;
  }

  open = () => {
    if (this.isDrawerOpen()) return;

    // Upgraded but not yet initialized (e.g. the move-to-body re-connect is
    // still pending): queue the intent instead of dropping the tap.
    // initializeDrawer() consumes the queue (GC-2437).
    if (!this.isInitialized) {
      this.pendingOpen = true;
      return;
    }

    this.focusedElementBeforeOpen = document.activeElement;
    const lock = scrollLock;
    if (lock && typeof lock.enabledBodyScrollLock === 'function') {
      lock.enabledBodyScrollLock();
    }

    document.addEventListener('keydown', this.escapeListener);

    requestAnimationFrame(() => {
      this.classList.add('open');
      this.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      document.body.classList.add('overflow-hidden');

      if (typeof trapFocus === 'function') {
        trapFocus(this.container);
      }

      if (typeof getFocusableElements === 'function') {
        const firstFocusableElement = getFocusableElements(this.container)[0];
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
      }

      this.dispatchEvent(new CustomEvent('slide-out-drawer:open', { bubbles: true }));
    });
  }

  close = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (!this.isInitialized || !this.isDrawerOpen()) return;

    // Remove the body's scroll-lock class BEFORE calling disableBodyScrollLock()
    // below, not after, and not deferred to animationend. `body.overflow-hidden`
    // (base.css) sets `position: fixed` with no `top`. disableBodyScrollLock()
    // clears ScrollLock's own inline `position`/`top` and then immediately calls
    // `window.scrollTo()` to restore the real scroll position — but while this
    // class is still applied, the body is still effectively `position: fixed`
    // (via the class, not the inline style just removed), and a `scrollTo()`
    // call against a fixed-positioned body silently no-ops. That's the "jumps
    // to the top of the PLP" bug: `scrollTo()` runs, is passed the correct
    // captured position, and is discarded anyway. Removing this class first
    // ensures the body is genuinely back in normal flow before ScrollLock
    // tries to restore scroll position against it.
    document.body.classList.remove('overflow-hidden');

    const lock = scrollLock;
    if (lock && typeof lock.disableBodyScrollLock === 'function') {
      lock.disableBodyScrollLock(this.shouldRestoreScrollOnClose());
    }

    document.removeEventListener('keydown', this.escapeListener);
    this.classList.add('closing');
    this.classList.remove('open');

    const handleAnimationEnd = () => {
      this.classList.remove('closing');
      this.setAttribute('aria-hidden', 'true');
      this.isOpen = false;

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }

      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
      this.dispatchEvent(new CustomEvent('slide-out-drawer:close', { bubbles: true }));
      this.removeEventListener('animationend', handleAnimationEnd);
    };

    this.addEventListener('animationend', handleAnimationEnd);
  }

  toggle = () => {
    this.isOpen ? this.close() : this.open();
  }

  isDrawerOpen = () => this.isOpen

  /**
   * Whether close() should restore the scroll position captured when this
   * drawer opened. True by default, matching every existing SlideOutDrawer
   * consumer (cart, menu, search). A subclass overrides this to return false
   * when it has already deliberately moved the page elsewhere during this
   * open session - e.g. FilterDrawer, after an applied filter/sort/clear
   * scrolls the grid to the top (GC-2437) - so this restore doesn't undo
   * that newer, more relevant scroll position.
   * @returns {boolean}
   */
  shouldRestoreScrollOnClose = () => true
}

if (!customElements.get('slide-out-drawer')) {
  customElements.define('slide-out-drawer', SlideOutDrawer);
}

export class SlideOutDrawerOpener extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.button = this.querySelector('button');
    if (!this.button) {
      console.error('Slide-out drawer opener: Button not found');
      return;
    }

    this.drawerId = this.dataset.drawer || 'slide-out-drawer-null';
    this.drawer = document.querySelector(`.slide-out-drawer#${this.drawerId}`);

    if (this.drawer) {
      this.handleDrawerClose = () => {
        this.button.setAttribute('aria-expanded', 'false');
      };
      this.handleDrawerOpen = () => {
        this.button.setAttribute('aria-expanded', 'true');
      };
      this.drawer.addEventListener('slide-out-drawer:close', this.handleDrawerClose);
      this.drawer.addEventListener('slide-out-drawer:open', this.handleDrawerOpen);
    } else {
      console.error(`Slide-out drawer opener: Drawer with id "${this.drawerId}" not found or is not a SlideOutDrawer instance`);
    }

    // Not debounced: open() is itself idempotent (it no-ops when the drawer
    // is already open or not yet initialized), so a debounce here added no
    // real protection - it only delayed every tap by its full wait period
    // and, on repeated taps, pushed the open further out each time instead
    // of honoring the first one. That read as "it takes a few taps to open"
    // (GC-2437), worst on a slow connection where a shopper is more likely
    // to tap again after seeing nothing happen right away.
    this.handleClick = () => {
      if (this.drawer && this.drawer instanceof SlideOutDrawer) {
        this.drawer.open();
      } else {
        console.error(`Slide-out drawer opener: Drawer with id "${this.drawerId}" not found or is not a SlideOutDrawer instance`);
      }
    };

    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button && this.handleClick) {
      this.button.removeEventListener('click', this.handleClick);
    }

    if (this.drawer && this.handleDrawerClose) {
      this.drawer.removeEventListener('slide-out-drawer:close', this.handleDrawerClose);
    }

    if (this.drawer && this.handleDrawerOpen) {
      this.drawer.removeEventListener('slide-out-drawer:open', this.handleDrawerOpen);
    }
  }
}

if (!customElements.get('slide-out-drawer-opener')) {
  customElements.define('slide-out-drawer-opener', SlideOutDrawerOpener);
}
